 
/* eslint-disable indent */
 
import * as Yup from 'yup';
import { IOptionAnyValue } from 'bricks/types';
import { UnitTypeIds } from 'constants/unitTypes';
import {
    OperationExpenseType, STRUCTURED_PARKING, SURFACE_PARKING, TRAILER_PARKING, UNDERGROUND_PARKING,
} from 'views/ProFormaTable/constants';
import { isDebtSource } from 'utils/sourceTypeUtils';
import { ISourceType } from 'views/ProFormaTable/types';
import { ParkingKey } from './types';
import { StepOneValues } from './components/ProFormaWizardStepOne/ProFormaWizardStepOne';

export const UNIT_MIX_DEFAULT_NAME = 'Default name';

export const PROPERTY_TAXES = 'Property taxes';
export const OPERATING_EXPENSES = 'Operating expenses';
export const DEFAULT_EXPENSE_ANNUAL_GROWTH = 0.03;

export const INTEREST_RESERVE_WIZARD_TEXT = `The interest reserve will be calculated in the final pro forma. 
    As a result, it — along with the developer fee, total uses, construction debt (if calculated based on LTC), and total sources — 
    may be subject to adjustment in the final pro forma.`;

export const INITIAL_EXPENSES = [
    {
        id: 1, name: 'Capital reserves', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
    {
        id: 2, name: 'Contract services', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
    {
        id: 3, name: 'General and administrative', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
    {
        id: 4, name: 'Insurance', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 5, name: 'Make ready cost', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 6, name: 'Management fee', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 7, name: 'Marketing', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 8, name: 'Other capital expenditures', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 9, name: 'Payroll', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0,
    },
    {
        id: 10,
        name: PROPERTY_TAXES,
        annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH,
        amountPerYear: 0,
        operationExpenseType: OperationExpenseType.PROPERTY_TAXES,
        isDeleteAllowed: false,
    },
    {
        id: 11, name: 'Repairs and maintenance', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
    {
        id: 12, name: 'Retail leasing cost reserves', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
    {
        id: 13, name: 'Utilities', annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH, amountPerYear: 0, isDeleteAllowed: true,
    },
];

export const stepOneValidationSchema = Yup.object().shape({
    buildings: Yup.number()
        .required('Number of buildings is required')
        .min(1, 'Number of buildings must be greater than 0')
        .integer('Must be a whole number'),
    builtGrossSquareFootage: Yup.number().required('Built gross SF is required').min(1, 'Built gross SF must be greater than 0'),
    stories: Yup.number().required('Number of stories is required').min(1, 'Number of stories must be greater than 0').integer('Must be a whole number'),
    sellingCosts: Yup.number()
        .required('Selling costs is required')
        .min(0.01, 'Selling costs cannot be less than 1')
        .max(1, 'Selling costs cannot exceed 100%'),
    projectedCapRateAtSale: Yup.number()
        .required('Projected cap rate at sale is required')
        .min(0.01, 'Projected cap rate at sale cannot be less than 1')
        .max(1, 'Projected cap rate at sale cannot exceed 100%'),
    currentMarketCapRate: Yup.number()
        .required('Current market cap rate is required')
        .min(0.01, 'Current market cap rate cannot be less than 1')
        .max(1, 'Current market cap rate cannot exceed 100%'),
    structuredParkingSpaces: Yup.number().nullable().when(['parking'], ([parking]: IOptionAnyValue[][]) => {
        const parkingArray = parking || [];
        return parkingArray.flat().some(p => p.value === STRUCTURED_PARKING)
            ? Yup.number()
                .required('Structured parking spaces is required')
                .test('min-value', 'Structured parking spaces cannot be less than 1', value => value === undefined || value === null || value >= 1)
                .integer('Must be a whole number')
            : Yup.number().nullable();
    }),
    surfaceParkingSpaces: Yup.number().nullable().when(['parking'], ([parking]: IOptionAnyValue[][]) => {
        const parkingArray = parking || [];
        return parkingArray.flat().some(p => p.value === SURFACE_PARKING)
            ? Yup.number()
                .required('Surface parking spaces is required')
                .test('min-value', 'Surface parking spaces cannot be less than 1', value => value === undefined || value === null || value >= 1)
                .integer('Must be a whole number')
            : Yup.number().nullable();
    }),
    trailerParkingSpaces: Yup.number().nullable().when(['parking'], ([parking]: IOptionAnyValue[][]) => {
        const parkingArray = parking || [];
        return parkingArray.flat().some(p => p.value === TRAILER_PARKING)
            ? Yup.number()
                .required('Trailer parking spaces is required')
                .test('min-value', 'Trailer parking spaces cannot be less than 1', value => value === undefined || value === null || value >= 1)
                .integer('Must be a whole number')
            : Yup.number().nullable();
    }),
    undergroundParkingSpaces: Yup.number().nullable().when(['parking'], ([parking]: IOptionAnyValue[][]) => {
        const parkingArray = parking || [];
        return parkingArray.flat().some(p => p.value === UNDERGROUND_PARKING)
            ? Yup.number()
                .required('Underground parking spaces is required')
                .test('min-value', 'Underground parking spaces cannot be less than 1', value => value === undefined || value === null || value >= 1)
                .integer('Must be a whole number')
            : Yup.number().nullable();
    }),
});

export const stepTwoValidationSchema = Yup.object().shape({
    acquisitionCosts: Yup.number().min(1, 'Must be greater than 0').required('Required'),
    hardCosts: Yup.number().nullable(),
    totalHardCosts: Yup.number().min(0.01, 'Must be greater than 0').required('Required'),
    hardCostType: Yup.string().oneOf(['sf', 'unit', 'total'], 'Invalid cost type').required('Required'),
    softCosts: Yup.number().nullable(),
    totalSoftCosts: Yup.number().min(0.01, 'Must be greater than 0').required('Required'),
    softCostType: Yup.string().oneOf(['sf', 'unit', 'total'], 'Invalid cost type').required('Required'),
    developerFee: Yup.number().min(0.01, 'Must be greater than 0').required('Required'),
    developerFeePercent: Yup.number().min(0, 'Must be greater than 0').max(1, 'Must be less than or equal to 100').required('Required'),
    interestReserve: Yup.number()
        .required('Required')
        .test(
            'interestReserve-conditional',
            'Must be greater than 0',
            (value, context) => {
                const sources = context?.parent.sources;
                const sourceTypes = (context?.options?.context as { sourceTypes?: ISourceType[] })?.sourceTypes;
                const hasDebt = Array.isArray(sources) && sources.some(
                    s => isDebtSource(s.sourceTypeId, sourceTypes),
                );
                if (hasDebt) {
                    return value > 0;
                }
                // if no debt, 0 or positive is fine
                return value >= 0;
            },
        ),
    sources: Yup.array()
        .of(
            Yup.object().shape({
                sourceTypeId: Yup.string().required('Source type is required'),
                name: Yup.string().required('Source name is required'),
                traunch: Yup.string().required('Traunch is required'),
                amount: Yup.number().required('Amount is required'),
            }),
        ),
});

export const PRO_FORMA_WIZARD_STEPS = {
    STEP_1: 1,
    STEP_2: 2,
    STEP_3: 3,
};

export const parkingTypesFieldMapping: {
  key: ParkingKey;
  field: keyof StepOneValues;
}[] = [
  { key: STRUCTURED_PARKING, field: 'structuredParkingSpaces' },
  { key: SURFACE_PARKING, field: 'surfaceParkingSpaces' },
  { key: TRAILER_PARKING, field: 'trailerParkingSpaces' },
  { key: UNDERGROUND_PARKING, field: 'undergroundParkingSpaces' },
];

export const LAND_AREA_UNITS = [
    { value: 'acres', label: 'acres' },
    { value: 'sqft', label: 'ft\u00B2' },
];

export enum StepOneUnitName {
    ACRES = 'acres',
    SQUARE_FEET = 'sqft',
}

export const LandAreaUnitTypeNameToId: Record<string, number> = {
  [StepOneUnitName.ACRES]: UnitTypeIds.ACRE,
  [StepOneUnitName.SQUARE_FEET]: UnitTypeIds.GROSS_SF,
};

export const LandAreaUnitTypeIdToName: Record<number, string> = {
  [UnitTypeIds.ACRE]: StepOneUnitName.ACRES,
  [UnitTypeIds.GROSS_SF]: StepOneUnitName.SQUARE_FEET,
};

export enum AreaMode {
    FAR = 'FAR',
    GROSS_BUILD_AREA = 'GROSS_BUILD_AREA'
}

export enum OperatingExpenseCalculatedOn {
    PERCENTAGE_OF_EFFECTIVE_GROSS_REVENUE = 'percentage_of_effective_gross_revenue',
    VALUE_PER_UNIT_PER_YEAR = 'value_per_unit_per_year',
    VALUE_PER_SF_PER_YEAR = 'value_per_sf_per_year',
    AMOUNT_PER_YEAR = 'amount_per_year',
    MILL_RATE = 'mill_rate',
}

export enum IncomeMiscCalculatedOn {
    AMOUNT_PER_YEAR = 'amount_per_year',
    PER_UNIT_PER_MONTH = 'value_per_unit_per_month'
}
